// Itinerary maths (Figma C1b): which bike, when you leave / reach the bike / arrive, estimated price.
import type { Directions, DirectionsMode, ParkingZone, Position, Vehicle } from "../shared/api";
import { type PricingPlan, priceForPlan } from "../shared/pricing";
import { distanceMeters } from "./state/geo";

/** The nearest bike that can be booked (it has a stable Pony id), by straight-line distance. */
export function pickBike(user: Position, vehicles: Vehicle[]): Vehicle | undefined {
  let best: Vehicle | undefined;
  let bestD = Number.POSITIVE_INFINITY;
  for (const v of vehicles) {
    if (!v.vehicleId) continue;
    const d = distanceMeters(user, v);
    if (d < bestD) {
      best = v;
      bestD = d;
    }
  }
  return best;
}

const MIN = 60_000;

/** Clock times of the trip, rounded up to the minute (never promise an earlier arrival). */
export function itineraryTimes(nowMs: number, walkSec: number, rideSec: number, timeZone?: string) {
  const fmt = new Intl.DateTimeFormat("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
    ...(timeZone && { timeZone }),
  });
  const up = (ms: number) => Math.ceil(ms / MIN) * MIN;
  const start = Math.floor(nowMs / MIN) * MIN;
  return {
    departure: fmt.format(start),
    bikeAt: fmt.format(up(start + walkSec * 1000)),
    arrival: fmt.format(up(start + (walkSec + rideSec) * 1000)),
    totalMin: Math.ceil((walkSec + rideSec) / 60),
  };
}

/** Estimated ride price from the bike's GBFS plan (unlock + every started interval). */
export const rideEstimateCents = (plan: PricingPlan, rideSec: number) =>
  priceForPlan(plan, 0, rideSec * 1000);

// ---- Bike-only itinerary (Figma C1 / C1b / C5 / C7): walk → Pony bike → parking → walk ----------

export interface Place {
  name: string;
  position: Position;
}
export interface Leg {
  sec: number;
  meters: number;
  path: Position[];
}
/** One result of the C1 list: a bike, the parking nearest the destination, three legs. */
export interface ItineraryPlan {
  from: Place;
  to: Place;
  bike: Vehicle;
  parking: ParkingZone;
  walk: Leg;
  ride: Leg;
  last: Leg;
  /** Bookable bikes around the start (Figma "3 vélos disponibles à 80 m"). */
  bikesNearby: number;
}

/** The `n` nearest bookable bikes (with a Pony id), nearest first: the C1 candidates. */
export function candidateBikes(from: Position, vehicles: Vehicle[], n = 3): Vehicle[] {
  return vehicles
    .filter((v) => v.vehicleId)
    .map((v) => ({ v, d: distanceMeters(from, v) }))
    .sort((a, b) => a.d - b.d)
    .slice(0, n)
    .map((x) => x.v);
}

/** Bookable bikes within `radius` metres of a point. */
export const bikesAround = (at: Position, vehicles: Vehicle[], radius: number) =>
  vehicles.filter((v) => v.vehicleId && distanceMeters(at, v) <= radius).length;

/** Where the ride ends: the parking zone nearest the destination. */
export function nearestParking(to: Position, zones: ParkingZone[]): ParkingZone | undefined {
  let best: ParkingZone | undefined;
  let bestD = Number.POSITIVE_INFINITY;
  for (const z of zones) {
    const d = distanceMeters(to, z);
    if (d < bestD) {
      best = z;
      bestD = d;
    }
  }
  return best;
}

/** Clock times of the three legs, each rounded up to the minute (never promise earlier). */
export function planTimes(nowMs: number, p: ItineraryPlan, timeZone?: string) {
  const fmt = new Intl.DateTimeFormat("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
    ...(timeZone && { timeZone }),
  });
  const start = Math.floor(nowMs / MIN) * MIN;
  const at = (sec: number) => Math.ceil((start + sec * 1000) / MIN) * MIN;
  const total = p.walk.sec + p.ride.sec + p.last.sec;
  return {
    departure: fmt.format(start),
    bikeAt: fmt.format(at(p.walk.sec)),
    parkAt: fmt.format(at(p.walk.sec + p.ride.sec)),
    arrival: fmt.format(at(total)),
    totalMin: Math.ceil(total / 60),
  };
}

const totalSec = (p: ItineraryPlan) => p.walk.sec + p.ride.sec + p.last.sec;
const walkSec = (p: ItineraryPlan) => p.walk.sec + p.last.sec;

/** C1 filters: "Le plus rapide" (total time) or "Le moins de marche à pied" (walking time). */
export type PlanOrder = "fastest" | "leastWalk";
export function sortPlans(plans: ItineraryPlan[], order: PlanOrder): ItineraryPlan[] {
  const [first, then] = order === "fastest" ? [totalSec, walkSec] : [walkSec, totalSec];
  return [...plans].sort((a, b) => first(a) - first(b) || then(a) - then(b));
}

const euros = (cents: number) => `${(cents / 100).toFixed(2).replace(".", ",")} €`;

/** C1b price line from the bike's GBFS plan: "≈ 4,04 €" and "(1,00 € + 16 min × 0,19 €)". */
export function priceDetail(plan: PricingPlan, rideSec: number) {
  const min = Math.max(1, Math.ceil(rideSec / 60));
  const rate = plan.segments[0];
  const perMin =
    rate && rate.intervalMin === 1 ? `${min} min × ${euros(rate.rateCents)}` : `${min} min`;
  return {
    total: `≈ ${euros(rideEstimateCents(plan, rideSec))}`,
    detail: `(${plan.unlockCents > 0 ? `${euros(plan.unlockCents)} + ` : ""}${perMin})`,
  };
}

/** "Stationnement Odysseum" → "Odysseum". */
export const parkingShortName = (name: string) => name.replace(/^Stationnement\s+/i, "");

/** Figma C5 / C7 step banner while an itinerary is followed (walk / bike / walk). */
export function bannerFor(p: ItineraryPlan, phase: "booked" | "riding") {
  return phase === "booked"
    ? { overline: "Étape 1 sur 3 · Vélo Pony", title: "Rejoignez votre vélo réservé" }
    : {
        overline: `Étape 2 sur 3 · ${Math.max(1, Math.ceil(p.ride.sec / 60))} min à vélo`,
        title: `Garez-vous au stationnement ${parkingShortName(p.parking.name)}`,
      };
}

type Route = (a: Position, b: Position, mode: DirectionsMode) => Promise<Directions>;
const toLeg = (d: Directions): Leg => ({
  sec: d.durationSec,
  meters: d.distanceMeters,
  path: d.path,
});

/**
 * The C1 results: for each candidate bike, walk to it, ride to the parking nearest the destination,
 * walk on. Candidates whose routes fail are left out. Throws NO_BIKE, NO_PARKING or NO_ROUTE.
 */
export async function computePlans(o: {
  from: Place;
  to: Place;
  vehicles: Vehicle[];
  zones: ParkingZone[];
  route: Route;
}): Promise<{ plans: ItineraryPlan[]; walkOnlySec?: number }> {
  const bikes = candidateBikes(o.from.position, o.vehicles);
  if (bikes.length === 0) throw new Error("NO_BIKE");
  const parking = nearestParking(o.to.position, o.zones);
  if (!parking) throw new Error("NO_PARKING");
  const [last, walkOnly, ...legs] = await Promise.allSettled([
    o.route(parking, o.to.position, "foot"),
    o.route(o.from.position, o.to.position, "foot"),
    ...bikes.map((b) =>
      Promise.all([o.route(o.from.position, b, "foot"), o.route(b, parking, "bicycle")]),
    ),
  ]);
  if (last.status === "rejected") throw new Error("NO_ROUTE");
  const plans: ItineraryPlan[] = [];
  legs.forEach((r, i) => {
    const bike = bikes[i];
    if (r.status !== "fulfilled" || !bike) return;
    const [walk, ride] = r.value as [Directions, Directions];
    plans.push({
      from: o.from,
      to: o.to,
      bike,
      parking,
      walk: toLeg(walk),
      ride: toLeg(ride),
      last: toLeg(last.value),
      bikesNearby: bikesAround(bike, o.vehicles, 150),
    });
  });
  if (plans.length === 0) throw new Error("NO_ROUTE");
  return {
    plans,
    ...(walkOnly.status === "fulfilled" && { walkOnlySec: walkOnly.value.durationSec }),
  };
}
